import { useEffect, useState, useCallback } from 'react';
import { useUI } from 'store/UI/selectors';
import { useCurrentUser } from 'store/users/selectors';
import config from '../../../config';
import { useDispatch } from 'react-redux';
import { updateUI } from 'store/UI/actions';
import { handleAddInappropriateAlert } from 'store/logs/thunks';
import { useCurrentClass } from 'store/classes/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { debounce } from 'lodash';

const getContextFromLocation = (location) => {
  if (!location) return null;
  const locationLower = location.toLowerCase();
  if (locationLower.includes('vocab') || locationLower.includes('cognate') || locationLower.includes('phrase')) {
    return 'vocabulary';
  }
  if (locationLower.includes('story')) {
    return 'story';
  }
  if (locationLower.includes('assignment')) {
    return 'assignment';
  }
  return null;
};

const getUrls = (context) => {
  const isDev = config.isDev;
  const baseUrl = 'https://storage.googleapis.com/yo-americano.appspot.com/json_data/';
  
  if (context === 'common') {
    return {
      wordsOnly: isDev 
        ? `${baseUrl}prohibated_word_test.json`
        : `${baseUrl}prohibated_word.json`,
      wordsWithSynonyms: isDev
        ? `${baseUrl}prohibated_word_with_synonyms_test.json`
        : `${baseUrl}prohibated_word_with_synonyms.json`
    };
  }
  
  return {
    wordsOnly: `${baseUrl}prohibated_word_${context}_${isDev ? 'test' : 'prod'}.json`,
    wordsWithSynonyms: `${baseUrl}prohibated_word_${context}_with_synonyms_${isDev ? 'test' : 'prod'}.json`
  };
};

const fetchWordsWithSynonyms = async (url) => {
  try {
    const response = await fetch(`${url}?t=${Date.now()}`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data.map(item => ({
      word: typeof item === 'string' ? item.toLowerCase() : (item.word || '').toLowerCase(),
      synonyms: typeof item === 'string' ? [] : (Array.isArray(item.synonyms) ? item.synonyms : [])
    })) : [];
  } catch (error) {
    console.error(`Error fetching words with synonyms from ${url}:`, error);
    return [];
  }
};

const useBadWords = (location = null) => {
  const currentUser = useCurrentUser();
  const { badWords = [], wordsWithSynonyms = [], contextWords = [] } = useUI();
  const dispatch = useDispatch();
  const currentClass = useCurrentClass();
  const showAlert = useAlert();
  const [alreadyFetched, setAlreadyFetched] = useState(false);

  const fetchBadWords = useCallback(async () => {
    try {
      if (alreadyFetched) return;
      const commonUrls = getUrls('common');
      const response = await fetch(`${commonUrls.wordsOnly}?t=${Date.now()}`);
      const data = await response.json();
      const words = Array.isArray(data) ? data.map(item => 
        typeof item === 'string' ? item.toLowerCase() : (item.word || '').toLowerCase()
      ) : [];
      await dispatch(updateUI({ badWords: words }));
      setAlreadyFetched(true);
    } catch (error) {
      console.log(error);
    }
  }, [dispatch, alreadyFetched]);

  const debouncedFetchBadWords = useCallback(
    debounce(() => {
      fetchBadWords();
    }, 1000),
    [fetchBadWords]
  );

  const loadWordsWithSynonyms = useCallback(async () => {
    try {
      const commonUrls = getUrls('common');
      const commonData = await fetchWordsWithSynonyms(commonUrls.wordsWithSynonyms);
      
      let contextData = [];
      if (location) {
        const context = getContextFromLocation(location);
        if (context) {
          const contextUrls = getUrls(context);
          contextData = await fetchWordsWithSynonyms(contextUrls.wordsWithSynonyms);
        }
      }

      await dispatch(updateUI({ wordsWithSynonyms: commonData, contextWords: contextData }));
    } catch (error) {
      console.error('Error loading words with synonyms:', error);
    }
  }, [location]);

  const checkBadWords = (text, alertLocationOrIsShowAlert = null, showBlockWords = false) => {
    try {
      if (!text || typeof text !== 'string') {
        return {
          isFound: false,
          foundWords: [],
        };
      }
      
      const textLower = text.toLowerCase();
      const textNormalized = textLower.replace(/[^\w\s\u00C0-\u017F]/gi, ' ').trim();
      const wordsInText = new Set(textNormalized.split(/\s+/).filter(word => word.length > 0));
      
      const allWords = [...badWords, ...contextWords.map(item => item.word)];
      const foundWords = [];
      
      allWords.forEach((word) => {
        if (!word || typeof word !== 'string') return;
        
        const wordLower = word.toLowerCase();
        const normalizedWord = wordLower.trim();
        if (normalizedWord.length === 0) return;
        
        const hasSpecialChars = /[^\w\s]/.test(word);
        
        if (hasSpecialChars) {
          if (textLower.includes(wordLower)) {
            foundWords.push(word);
          }
          return;
        }
        
        const wordNormalized = normalizedWord.replace(/[^\w\s\u00C0-\u017F]/gi, '');
        
        if (wordNormalized.includes(' ')) {
          const normalizedPhrase = wordNormalized.replace(/[^\w\s]/gi, ' ').trim();
          if (textNormalized.includes(normalizedPhrase)) {
            foundWords.push(word);
          }
        } else {
          const singleWord = wordNormalized.replace(/[^\w]/gi, '');
          if (singleWord.length <= 1) {
            return;
          }
          if (wordsInText.has(singleWord)) {
            foundWords.push(word);
          }
        }
      });
      
      if (foundWords?.length > 0) {
        const isLegacyMode = typeof alertLocationOrIsShowAlert === 'boolean';
        const alertLocation = typeof alertLocationOrIsShowAlert === 'string' ? alertLocationOrIsShowAlert : null;
        const isShowAlert = typeof alertLocationOrIsShowAlert === 'boolean' ? alertLocationOrIsShowAlert : null;
        
        handleAddInappropriateAlert(foundWords, currentUser, currentClass, alertLocation || location || 'student submission', text);
        
        if (isLegacyMode && isShowAlert && showBlockWords) {
          showAlert(`Please remove the bad words(${foundWords?.join(', ')}) from your message`, 'error');
        } else if (isLegacyMode && isShowAlert && !showBlockWords) {
          showAlert(`Please remove the bad words from your message`, 'error');
        } else if (!isLegacyMode) {
          const foundWordsWithSynonyms = [];
          foundWords.forEach(foundWord => {
            const commonMatch = wordsWithSynonyms.find(item => item.word === foundWord);
            const contextMatch = contextWords.find(item => item.word === foundWord);
            const match = commonMatch || contextMatch;
            if (match) {
              foundWordsWithSynonyms.push({
                word: foundWord,
                synonym: match.synonyms && match.synonyms.length > 0 ? match.synonyms[0] : null
              });
            } else {
              foundWordsWithSynonyms.push({
                word: foundWord,
                synonym: null
              });
            }
          });
          
          const wordsWithSynonymText = foundWordsWithSynonyms
            .filter(item => item.synonym)
            .map(item => `"${item.word}" (use "${item.synonym}" instead)`)
            .join(', ');

          
          const wordsWithoutSynonym = foundWordsWithSynonyms
            .filter(item => !item.synonym)
            .map(item => `"${item.word}"`)
            .join(', ');
          
          let alertMessage = 'Please use appropriate words. ';
          if (wordsWithSynonymText) {
            alertMessage += wordsWithSynonymText;
          }
          if (wordsWithoutSynonym) {
            if (wordsWithSynonymText) {
              alertMessage += '. Also avoid: ' + wordsWithoutSynonym;
            } else {
              alertMessage += 'Avoid these words: ' + wordsWithoutSynonym;
            }
          }
          
          showAlert(alertMessage, 'warning');
        }
      }
      
      return {
        isFound: foundWords?.length > 0,
        foundWords,
      };
    } catch (error) {
      console.log(error);
      return {
        isFound: false,
        foundWords: [],
      };
    }
  };

  const getTextFromHtml = (html) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;
    return tempElement.textContent || tempElement.innerText;
  };

  return {
    badWords,
    checkBadWords,
    getTextFromHtml,
    fetchBadWords: debouncedFetchBadWords,
    loadWordsWithSynonyms
  };
};

export default useBadWords;
